'use strict'

// functions-shared/utils/testCustomers.js
//
// THE test-customer definition (docs/features/test_customer_sweep_FEATURE.md §2.1): a customer is a test customer when
// their display name ENDS WITH one of the configured names, as whole words, ignoring case. The names live in
// globalSettings/testCustomers.markers (Settings → Test Customers) and default to ['Tester'], the house rule before the
// list existed. Pure: the server loads the list (testCustomerConfig.js) and the website imports this file
// (src/utils/testCustomer.js), so both sides match with the same code.
//
// NOT the test-walk fence. What an AI test session may touch stays fixed to last name exactly "Tester"
// (agentOutboundFence.isTesterCustomerDoc): a name added here must never widen it.

const { getCustomerDisplayName } = require('./customerDisplayName')

const TEST_CUSTOMERS_DOC = 'globalSettings/testCustomers'
const DEFAULT_TEST_MARKERS = Object.freeze(['Tester'])
const MARKER_MIN = 2
const MARKER_MAX = 40
const MAX_MARKERS = 20

// Words of a name: quotes dropped (`Lily "Bad Tester" 1` was a real record), split on whitespace, lowercased.
function words(text) {
  return String(text || '')
    .replace(/["“”]/g, ' ')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
}

/** One entry tidied for storage: inner whitespace collapsed, quotes dropped, ends trimmed. */
function tidyMarker(marker) {
  return String(marker == null ? '' : marker).replace(/["“”]/g, ' ').replace(/\s+/g, ' ').trim()
}

/** Why an entry cannot be saved, or null. */
function markerProblem(marker) {
  const m = tidyMarker(marker)
  if (m.length < MARKER_MIN) return `A test name needs at least ${MARKER_MIN} characters.`
  if (m.length > MARKER_MAX) return `A test name can be at most ${MARKER_MAX} characters.`
  return null
}

/** The list as stored: tidied, valid entries only, first spelling of each kept (case-insensitive). */
function normalizeMarkers(list) {
  if (!Array.isArray(list)) return [...DEFAULT_TEST_MARKERS]
  const seen = new Set()
  const out = []
  for (const raw of list) {
    const m = tidyMarker(raw)
    if (markerProblem(m) || seen.has(m.toLowerCase())) continue
    seen.add(m.toLowerCase())
    out.push(m)
  }
  return out.slice(0, MAX_MARKERS)
}

/** The list from the stored doc's data: an absent doc or field is the default ['Tester']; a saved empty list is empty. */
function markersFromConfig(data) {
  return Array.isArray(data && data.markers) ? normalizeMarkers(data.markers) : [...DEFAULT_TEST_MARKERS]
}

/** The configured names this customer's name ends with (empty when it is not a test customer). */
function matchedMarkers(customer, markers) {
  if (!Array.isArray(markers)) throw new TypeError('matchedMarkers needs the configured test names (loadTestMarkers)')
  if (!customer) return []
  const name = words(getCustomerDisplayName(customer))
  if (!name.length || (name.length === 1 && name[0] === 'unknown')) return []
  return markers.filter((marker) => {
    const want = words(marker)
    if (!want.length || want.length > name.length) return false
    const tail = name.slice(name.length - want.length)
    return want.every((w, i) => w === tail[i])
  })
}

/** Is this a test customer under the configured names? `markers` is required: the caller loads the list. */
function isTestCustomer(customer, markers) {
  return matchedMarkers(customer, markers).length > 0
}

module.exports = {
  TEST_CUSTOMERS_DOC,
  DEFAULT_TEST_MARKERS,
  MARKER_MIN,
  MARKER_MAX,
  MAX_MARKERS,
  tidyMarker,
  markerProblem,
  normalizeMarkers,
  markersFromConfig,
  matchedMarkers,
  isTestCustomer,
}
